import type {
  AutoBookEmptyRequestPreset,
  AutoBookRequestPreset,
  GETOrganization,
  GETOrgConfig,
  OrganizationSubscription,
  TerminalOrgConfig,
  UserRoleType,
} from '~/services/apiClient'
import { TerminalName } from '~/services/apiClient'

export class OrgConfig {
  terminals_to_not_prefill: Set<TerminalName>
  terminals_to_automatically_set_plate: Set<TerminalName>
  terminals_allowing_green_trucks_in_auto_book: Set<TerminalName>
  /**
   * The terminal entries exactly as the API sent them.
   *
   * `PUT /organizations/preferences/` replaces `terminals` wholesale — it setattrs
   * every field with no merge — so anything this model doesn't rebuild is destroyed on
   * the next save. Keeping the raw entries and merging our changes onto them means a
   * setting we don't model (today `has_private_slots`, tomorrow whatever the backend
   * adds) survives instead of being silently reset to its default.
   */
  private rawTerminals: TerminalOrgConfig[]
  autoBookPresets: AutoBookRequestPreset[]
  autoBookEmptyPresets: AutoBookEmptyRequestPreset[]
  autoBookNotificationEmails: string[]
  autoBookNotificationEmailsOak: string[]
  exportVesselScheduleNotificationEmails: string[]
  exportVesselScheduleNotificationEmailsOak: string[]
  defaultChassisType?: 'own' | 'pool'
  onlyManagementUsersCanEditConnections: boolean

  constructor(config: GETOrgConfig | undefined) {
    const terminals = config?.terminals || []
    this.autoBookPresets = config?.auto_book_presets || []
    this.autoBookEmptyPresets = config?.auto_book_empty_presets || []
    // Type-safe access to the new field
    this.autoBookNotificationEmails =
      config?.auto_book_notification_emails || []
    this.autoBookNotificationEmailsOak =
      config?.auto_book_notification_emails_oak || []
    this.exportVesselScheduleNotificationEmails =
      config?.export_vessel_schedule_notification_emails || []
    this.exportVesselScheduleNotificationEmailsOak =
      config?.export_vessel_schedule_notification_emails_oak || []
    this.defaultChassisType = config?.default_chassis_type ?? 'pool'
    this.onlyManagementUsersCanEditConnections =
      config?.only_management_users_can_edit_connections ?? false
    this.rawTerminals = terminals
    this.terminals_to_not_prefill = new Set()
    this.terminals_to_automatically_set_plate = new Set()
    this.terminals_allowing_green_trucks_in_auto_book = new Set()
    if (config?.prefill_apm_dummy_license_plates === false) {
      this.terminals_to_not_prefill.add(TerminalName.Apm)
    }
    for (const terminalConfig of terminals) {
      if (terminalConfig.prefill_license_plates === false) {
        this.terminals_to_not_prefill.add(terminalConfig.terminal)
      }
      if (
        terminalConfig.set_unused_license_plate_if_no_plate_set_as_appointment_approaches ===
        true
      ) {
        this.terminals_to_automatically_set_plate.add(terminalConfig.terminal)
      }
      if (terminalConfig.allow_green_trucks_in_auto_book === true) {
        this.terminals_allowing_green_trucks_in_auto_book.add(
          terminalConfig.terminal
        )
      }
    }
  }

  shouldPrefillLicensePlate(terminal: TerminalName): boolean {
    return !this.terminals_to_not_prefill.has(terminal)
  }

  shouldSetPlateIfNotSet(terminal: TerminalName): boolean {
    return this.terminals_to_automatically_set_plate.has(terminal)
  }

  toggleShouldPrefillLicensePlate(terminal: TerminalName): void {
    if (this.terminals_to_not_prefill.has(terminal)) {
      this.terminals_to_not_prefill.delete(terminal)
      // turn off automatic plate setting
      if (this.terminals_to_automatically_set_plate.has(terminal)) {
        this.terminals_to_automatically_set_plate.delete(terminal)
      }
    } else {
      this.terminals_to_not_prefill.add(terminal)
    }
  }

  /**
   * Whether Auto Book may pick a green/EV plate at this terminal. Off by default: at
   * APM a green plate can't be swapped off the appointment afterwards, which traps a
   * mixed fleet. All-EV fleets turn it on, or Auto Book has no plates to choose from.
   */
  allowsGreenTrucksInAutoBook(terminal: TerminalName): boolean {
    return this.terminals_allowing_green_trucks_in_auto_book.has(terminal)
  }

  toggleAllowGreenTrucksInAutoBook(terminal: TerminalName): void {
    if (this.terminals_allowing_green_trucks_in_auto_book.has(terminal)) {
      this.terminals_allowing_green_trucks_in_auto_book.delete(terminal)
    } else {
      this.terminals_allowing_green_trucks_in_auto_book.add(terminal)
    }
  }

  toggleShouldSetPlateIfNotSet(terminal: TerminalName): void {
    if (this.terminals_to_automatically_set_plate.has(terminal)) {
      this.terminals_to_automatically_set_plate.delete(terminal)
    } else {
      this.terminals_to_automatically_set_plate.add(terminal)
      // turn off the prefill license plate setting
      if (!this.terminals_to_not_prefill.has(terminal)) {
        this.terminals_to_not_prefill.add(terminal)
      }
    }
  }

  /**
   * Terminal entries to send back.
   *
   * An entry is emitted when the terminal already had one (so its unmodelled settings
   * are carried through untouched) or when any setting we do model is non-default.
   * Terminals that are entirely default stay absent, which is what the backend
   * synthesises anyway — so a pristine org keeps a clean, empty `terminals`.
   *
   * Previously this rebuilt every entry from scratch and then kept only those with
   * `prefill_license_plates === false`, which quietly dropped both `has_private_slots`
   * and any terminal whose only non-default setting was something else.
   */
  private buildTerminalConfigs(): TerminalOrgConfig[] {
    const existingByTerminal = new Map(
      this.rawTerminals.map((terminalConfig) => [
        terminalConfig.terminal,
        terminalConfig,
      ])
    )
    const configs: TerminalOrgConfig[] = []
    for (const terminal of Object.values(TerminalName)) {
      const prefillLicensePlates = !this.terminals_to_not_prefill.has(terminal)
      const setPlateIfNotSet =
        this.terminals_to_automatically_set_plate.has(terminal)
      const allowGreenTrucks =
        this.terminals_allowing_green_trucks_in_auto_book.has(terminal)
      const existing = existingByTerminal.get(terminal)
      const isAllDefault =
        prefillLicensePlates && !setPlateIfNotSet && !allowGreenTrucks
      if (!existing && isAllDefault) continue
      configs.push({
        ...existing,
        terminal,
        prefill_license_plates: prefillLicensePlates,
        set_unused_license_plate_if_no_plate_set_as_appointment_approaches:
          setPlateIfNotSet,
        allow_green_trucks_in_auto_book: allowGreenTrucks,
      })
    }
    return configs
  }

  toAPIFormat(): GETOrgConfig {
    return {
      prefill_apm_dummy_license_plates: !this.terminals_to_not_prefill.has(
        TerminalName.Apm
      ),
      terminals: this.buildTerminalConfigs(),
      auto_book_presets: this.autoBookPresets,
      auto_book_empty_presets: this.autoBookEmptyPresets,
      auto_book_notification_emails: this.autoBookNotificationEmails,
      auto_book_notification_emails_oak: this.autoBookNotificationEmailsOak,
      export_vessel_schedule_notification_emails:
        this.exportVesselScheduleNotificationEmails,
      export_vessel_schedule_notification_emails_oak:
        this.exportVesselScheduleNotificationEmailsOak,
      default_chassis_type: this.defaultChassisType,
      only_management_users_can_edit_connections:
        this.onlyManagementUsersCanEditConnections,
    }
  }
}

export class Organization {
  id: number
  name: string
  scac: string | null
  role?: UserRoleType
  config: OrgConfig
  subscriptions: OrganizationSubscription[]
  constructor(org: GETOrganization) {
    this.id = org.id
    this.name = org.name
    this.scac = org.scac || null
    this.role = org.role
    this.config = new OrgConfig(org.config)
    this.subscriptions = org.subscriptions
  }

  get description(): string {
    return `${this.name} (${this.scac})`
  }
}
